import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Save } from "lucide-react";
import ProviderDashboardLayout from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

/**
 * Cross-service availability editor — batch-updates the weekly_schedule for
 * ALL of the provider's services in one save. Handy for pros who don't want
 * to set hours per listing.
 */
export default function AvailabilityPage() {
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState({});
  const [schedule, setSchedule] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/services/mine/list");
      // Guarantee unique ids even if backend momentarily returns dupes (e.g. race).
      const seen = new Set();
      const uniq = [];
      for (const s of data) {
        if (s.id && !seen.has(s.id)) {
          seen.add(s.id);
          uniq.push(s);
        }
      }
      setServices(uniq);
      // Initialize schedule from first service (or default)
      const first = uniq[0];
      if (first && first.weekly_schedule) {
        setSchedule(first.weekly_schedule);
      } else {
        const init = {};
        DAYS.forEach((d) => { init[d] = { open: d !== "sunday", slots: d !== "sunday" ? [{ start: "09:00", end: "17:00" }] : [] }; });
        setSchedule(init);
      }
      setSelected(Object.fromEntries(uniq.map((s) => [s.id, true])));
    })();
  }, []);

  const toggleDay = (d) => {
    const cur = schedule[d] || { open: false, slots: [] };
    setSchedule({ ...schedule, [d]: { ...cur, open: !cur.open, slots: !cur.open && cur.slots.length === 0 ? [{ start: "09:00", end: "17:00" }] : cur.slots } });
  };
  const setSlot = (d, i, key, val) => {
    const cur = schedule[d];
    const slots = [...cur.slots];
    slots[i] = { ...slots[i], [key]: val };
    setSchedule({ ...schedule, [d]: { ...cur, slots } });
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const ids = services.filter((s) => selected[s.id]).map((s) => s.id);
      await Promise.all(ids.map((id) => api.patch(`/services/${id}`, { weekly_schedule: schedule })));
      toast.success(`Updated schedule on ${ids.length} service${ids.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error("Could not update availability");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProviderDashboardLayout>
      <div className="mb-8">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Availability</div>
        <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]" data-testid="availability-title">Working hours</h1>
        <p className="text-[#252A34]/70 mt-1 text-sm">Bulk-set your weekly hours across all (or some) of your services.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white divide-y" data-testid="availability-schedule">
          {DAYS.map((d) => (
            <div key={d} className="p-4 flex items-start gap-4">
              <label className="inline-flex items-center gap-2 w-32 shrink-0">
                <input type="checkbox" checked={!!schedule[d]?.open} onChange={() => toggleDay(d)} data-testid={`av-${d}-toggle`} className="h-4 w-4 rounded border-slate-300" />
                <span className="capitalize font-medium text-[#071b22]">{d}</span>
              </label>
              <div className="flex-1 space-y-2">
                {(schedule[d]?.slots || []).map((slot, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="time" value={slot.start} onChange={(e) => setSlot(d, i, "start", e.target.value)} data-testid={`av-${d}-slot-${i}-start`} className="rounded-lg border border-slate-200 px-2 py-1 text-sm" />
                    <span className="text-[#252A34]/50">–</span>
                    <input type="time" value={slot.end} onChange={(e) => setSlot(d, i, "end", e.target.value)} data-testid={`av-${d}-slot-${i}-end`} className="rounded-lg border border-slate-200 px-2 py-1 text-sm" />
                  </div>
                ))}
                {schedule[d]?.open && schedule[d]?.slots?.length === 0 && (
                  <div className="text-xs text-[#252A34]/50">Open with no defined slot — add slots in the wizard.</div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="availability-services-list">
          <div className="text-sm font-medium text-[#071b22] mb-3">Apply to services</div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {services.length === 0 && <div className="text-sm text-[#252A34]/60">No services yet.</div>}
            {services.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!selected[s.id]} onChange={(e) => setSelected({...selected, [s.id]: e.target.checked})} data-testid={`av-select-${s.id}`} className="h-4 w-4 rounded border-slate-300" />
                <span className="truncate">{s.title}</span>
              </label>
            ))}
          </div>
          <button onClick={saveAll} disabled={saving || services.length === 0} data-testid="availability-save" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-2.5 text-sm font-medium hover:bg-[#11485a] disabled:opacity-60 transition-colors">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save schedule"}
          </button>
        </div>
      </div>
    </ProviderDashboardLayout>
  );
}
